import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Book, Code, Zap, Shield, Puzzle, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const quickStartCards = [
  {
    title: 'Getting Started',
    description: 'Learn the basics and set up your first chatbot',
    icon: <Book className="h-6 w-6" />,
    href: '/docs/getting-started/installation',
    color: 'text-blue-600',
  },
  {
    title: 'Integration Guides',
    description: 'Embed chat widgets and connect with external systems',
    icon: <Code className="h-6 w-6" />,
    href: '/docs/integration-guides',
    color: 'text-green-600',
  },
  {
    title: 'Advanced Features',
    description: 'AI validation, PII handling, and streaming responses',
    icon: <Zap className="h-6 w-6" />,
    href: '/docs/advanced-features',
    color: 'text-purple-600',
  },
  {
    title: 'Customization',
    description: 'Theming, rich media, and visual customization',
    icon: <Palette className="h-6 w-6" />,
    href: '/docs/customization',
    color: 'text-pink-600',
  },
  {
    title: 'Technical Architecture',
    description: 'Understand the system design and components',
    icon: <Puzzle className="h-6 w-6" />,
    href: '/docs/technical-architecture',
    color: 'text-orange-600',
  },
  {
    title: 'Security & Compliance',
    description: 'PII handling, authentication, and best practices',
    icon: <Shield className="h-6 w-6" />,
    href: '/docs/advanced-features/pii-handling',
    color: 'text-red-600',
  },
];

const popularPages = [
  {
    title: 'Embedding Chat Widget',
    description: 'Add the chat widget to your website',
    href: '/docs/integration-guides/embedding-chat-widget',
  },
  {
    title: 'Webhook Integration',
    description: 'Connect with external systems using webhooks',
    href: '/docs/integration-guides/webhook-integration',
  },
  {
    title: 'AI Response Validation',
    description: 'Intelligent validation of user responses',
    href: '/docs/advanced-features/ai-response-validation',
  },
  {
    title: 'Theming System',
    description: 'Customize the appearance of your chatbots',
    href: '/docs/customization/theming-system',
  },
];

export default function DocsHome() {
  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          OfferChat.ai Documentation
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Everything you need to build, customize, and deploy intelligent chatbots 
          with advanced AI capabilities, secure PII handling, and seamless integrations.
        </p>
        <div className="flex items-center justify-center gap-4 mt-8">
          <Button size="lg" asChild>
            <Link to="/docs/getting-started/installation">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" size="lg" asChild>
            <Link to="/docs/api-reference">
              API Reference
            </Link>
          </Button>
        </div>
      </div>

      {/* Quick Start Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {quickStartCards.map((card) => (
          <Card key={card.title} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className={`inline-flex p-2 rounded-lg bg-gray-100 w-fit ${card.color}`}>
                {card.icon}
              </div>
              <CardTitle className="text-lg">{card.title}</CardTitle>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="ghost" className="p-0 h-auto" asChild>
                <Link to={card.href} className="flex items-center text-primary hover:text-primary/80">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Popular Pages */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Popular Documentation</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {popularPages.map((page) => (
            <Card key={page.title} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  <Link 
                    to={page.href}
                    className="text-primary hover:text-primary/80 flex items-center"
                  >
                    {page.title}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardTitle>
                <CardDescription className="text-sm">
                  {page.description}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>

      {/* Key Features */}
      <div className="bg-gray-50 rounded-lg p-8 space-y-6">
        <h2 className="text-2xl font-bold text-gray-900 text-center">
          Platform Highlights
        </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="text-center space-y-2">
            <div className="text-3xl font-bold text-primary">30+</div>
            <div className="text-sm text-gray-600">E2E Tests</div>
          </div>
          <div className="text-center space-y-2">
            <div className="text-3xl font-bold text-primary">8</div>
            <div className="text-sm text-gray-600">AI Providers</div>
          </div>
          <div className="text-center space-y-2">
            <div className="text-3xl font-bold text-primary">100%</div>
            <div className="text-sm text-gray-600">TypeScript</div>
          </div>
          <div className="text-center space-y-2">
            <div className="text-3xl font-bold text-primary">24/7</div>
            <div className="text-sm text-gray-600">Availability</div>
          </div>
        </div>
      </div>

      {/* Getting Help */}
      <div className="border rounded-lg p-6 bg-blue-50 border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Need Help?
        </h3>
        <p className="text-blue-800 mb-4">
          Can't find what you're looking for? We're here to help you succeed with OfferChat.ai.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/docs/troubleshooting">
              Troubleshooting Guide
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href="https://github.com/ScaledByDesign/offerchats/issues" target="_blank" rel="noopener noreferrer">
              GitHub Issues
            </a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/portal">
              Contact Support
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
